import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Database, Plus, RefreshCw, Trash2, ExternalLink, ChevronDown, X, Menu } from 'lucide-react';
import { useStore } from '../hooks/useStore';
import { repoApi, chartApi, sessionApi } from '../services/api';
import type { Chart, ChartVersion, Repository } from '../types';
import toast from 'react-hot-toast';
import clsx from 'clsx';

export default function HomePage() {
  const navigate = useNavigate();
  const { repositories, setRepositories, charts, setCharts } = useStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [showAddRepo, setShowAddRepo] = useState(false);
  const [newRepoName, setNewRepoName] = useState('');
  const [newRepoUrl, setNewRepoUrl] = useState('');
  const [selectedChart, setSelectedChart] = useState<Chart | null>(null);
  const [chartVersions, setChartVersions] = useState<ChartVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    loadRepositories();
  }, []);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadRepositories = async () => {
    try {
      setIsLoading(true);
      const repos = await repoApi.list();
      setRepositories(repos);
      if (repos.length > 0 && !selectedRepo) {
        setSelectedRepo(repos[0].name);
      }
    } catch (error) {
      toast.error('Failed to load repositories');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRepo && !charts[selectedRepo]) {
      loadCharts(selectedRepo);
    }
  }, [selectedRepo]);

  const loadCharts = async (repoName: string) => {
    try {
      setLoadingCharts(true);
      const repoCharts = await chartApi.list(repoName);
      setCharts({ ...charts, [repoName]: repoCharts as Chart[] });
    } catch (error) {
      toast.error(`Failed to load charts from ${repoName}`);
      console.error(error);
    } finally {
      setLoadingCharts(false);
    }
  };

  const handleRefreshRepo = async (repoName: string) => {
    try {
      toast.loading('Refreshing repository...', { id: 'refresh' });
      await repoApi.update(repoName);
      await loadCharts(repoName);
      toast.success('Repository refreshed!', { id: 'refresh' });
    } catch (error) {
      toast.error('Failed to refresh repository', { id: 'refresh' });
      console.error(error);
    }
  };

  const handleAddRepo = async () => {
    if (!newRepoName || !newRepoUrl) {
      toast.error('Please enter both name and URL');
      return;
    }

    try {
      toast.loading('Adding repository...', { id: 'add-repo' });
      await repoApi.add(newRepoName, newRepoUrl);
      await loadRepositories();
      setShowAddRepo(false);
      setNewRepoName('');
      setNewRepoUrl('');
      toast.success('Repository added!', { id: 'add-repo' });
    } catch (error) {
      toast.error('Failed to add repository', { id: 'add-repo' });
      console.error(error);
    }
  };

  const handleRemoveRepo = async (repo: Repository) => {
    if (repo.type === 'default') {
      toast.error('Cannot remove default repository');
      return;
    }

    try {
      await repoApi.remove(repo.name);
      await loadRepositories();
      toast.success('Repository removed');
    } catch (error) {
      toast.error('Failed to remove repository');
      console.error(error);
    }
  };

  const handleSelectChart = async (chart: Chart) => {
    setSelectedChart(chart);
    setSelectedVersion(chart.version);
    setLoadingVersions(true);
    try {
      const versions = await chartApi.getVersions(chart.repository, chart.name);
      setChartVersions(versions);
    } catch (error) {
      console.error('Failed to load versions:', error);
      // Still allow proceeding with the current version
      setChartVersions([{ version: chart.version, appVersion: chart.appVersion, created: '' }]);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleCreateSession = async () => {
    if (!selectedChart) return;
    try {
      toast.loading('Loading chart...', { id: 'load-chart' });
      const { session } = await sessionApi.create(
        selectedChart.repository,
        selectedChart.name,
        selectedVersion
      );
      toast.success('Chart loaded!', { id: 'load-chart' });
      setSelectedChart(null);
      navigate(`/editor/${session.id}`);
    } catch (error) {
      toast.error('Failed to load chart', { id: 'load-chart' });
      console.error(error);
    }
  };

  const handleCloseVersionModal = () => {
    setSelectedChart(null);
    setChartVersions([]);
    setSelectedVersion('');
  };

  const handleSelectRepo = (repoName: string) => {
    setSelectedRepo(repoName);
    setIsSidebarOpen(false); // Close sidebar on mobile after selection
  };

  const filteredCharts = selectedRepo && charts[selectedRepo]
    ? charts[selectedRepo].filter(chart =>
        chart.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chart.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const selectedRepoData = repositories.find(r => r.name === selectedRepo);

  return (
    <div className="h-full flex relative">
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar - Repositories */}
      <aside
        className={clsx(
          'bg-dark-850 flex flex-col border-r border-dark-700 z-50',
          // Mobile: full-screen overlay sidebar
          'fixed inset-y-0 left-0 w-72 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:w-64',
          isSidebarOpen ? 'translate-x-0 slide-in-left' : '-translate-x-full'
        )}
      >
        <div className="p-4 border-b border-dark-700">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-dark-300 uppercase tracking-wider">
              Repositories
            </h2>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowAddRepo(true)}
                className="p-1.5 hover:bg-dark-700 rounded transition-colors"
                title="Add Repository"
              >
                <Plus className="w-4 h-4 text-dark-400" />
              </button>
              {/* Close button for mobile */}
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="p-1.5 hover:bg-dark-700 rounded transition-colors lg:hidden"
                title="Close sidebar"
              >
                <X className="w-4 h-4 text-dark-400" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 scrollbar-hide-mobile">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <div className="spinner" />
            </div>
          ) : (
            <div className="space-y-1">
              {repositories.map((repo) => (
                <div
                  key={repo.name}
                  className={clsx(
                    'group flex items-center justify-between px-3 py-2.5 sm:py-2 rounded-lg cursor-pointer transition-colors',
                    selectedRepo === repo.name
                      ? 'bg-accent-primary/20 text-accent-primary'
                      : 'hover:bg-dark-700 text-dark-300'
                  )}
                  onClick={() => handleSelectRepo(repo.name)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Database className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate text-sm">{repo.name}</span>
                    {repo.type === 'custom' && (
                      <span className="text-xs bg-dark-600 px-1.5 py-0.5 rounded">
                        custom
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 sm:transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRefreshRepo(repo.name);
                      }}
                      className="p-1.5 hover:bg-dark-600 rounded"
                      title="Refresh"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                    {repo.type === 'custom' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveRepo(repo);
                        }}
                        className="p-1.5 hover:bg-dark-600 rounded text-accent-error"
                        title="Remove"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* Main content - Charts */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Search header */}
        <div className="p-3 sm:p-4 border-b border-dark-700">
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="mobile-menu-btn flex-shrink-0"
              aria-label="Open repositories menu"
            >
              <Menu className="w-5 h-5 text-dark-400" />
            </button>

            {/* Selected repo indicator for mobile */}
            <div className="lg:hidden flex items-center gap-2 min-w-0 flex-shrink-0">
              <Database className="w-4 h-4 text-accent-primary flex-shrink-0" />
              <span className="text-sm text-dark-200 truncate max-w-[100px]">
                {selectedRepoData?.name || 'Select repo'}
              </span>
            </div>

            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-500 pointer-events-none" />
              <input
                type="text"
                placeholder="Search charts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input !pl-10"
              />
            </div>
          </div>
        </div>

        {/* Charts grid */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 scrollbar-hide-mobile">
          {loadingCharts ? (
            <div className="flex justify-center py-8">
              <div className="spinner" />
            </div>
          ) : filteredCharts.length === 0 ? (
            <div className="text-center py-8 text-dark-500">
              {selectedRepo
                ? 'No charts found. Try a different search.'
                : 'Select a repository to browse charts.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 sm:gap-4">
              {filteredCharts.map((chart) => (
                <ChartCard
                  key={`${chart.repository}-${chart.name}`}
                  chart={chart}
                  onClick={() => handleSelectChart(chart)}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Add Repository Modal */}
      {showAddRepo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-xl p-5 sm:p-6 w-full max-w-md border border-dark-700 fade-in">
            <h3 className="text-lg font-semibold mb-4">Add Repository</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-dark-400 mb-1">Name</label>
                <input
                  type="text"
                  value={newRepoName}
                  onChange={(e) => setNewRepoName(e.target.value)}
                  placeholder="my-repo"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm text-dark-400 mb-1">URL</label>
                <input
                  type="text"
                  value={newRepoUrl}
                  onChange={(e) => setNewRepoUrl(e.target.value)}
                  placeholder="https://charts.example.com"
                  className="input"
                />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button
                  onClick={() => setShowAddRepo(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddRepo}
                  className="btn btn-primary"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version Selector Modal */}
      {selectedChart && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-dark-800 rounded-t-2xl sm:rounded-xl p-5 sm:p-6 w-full sm:max-w-md border-t sm:border border-dark-700 fade-in safe-area-bottom">
            {/* Bottom sheet handle for mobile */}
            <div className="bottom-sheet-handle sm:hidden" />

            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {selectedChart.icon ? (
                  <img
                    src={selectedChart.icon}
                    alt={selectedChart.name}
                    className="w-10 h-10 rounded-lg object-contain bg-dark-700 p-1"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-dark-700 flex items-center justify-center">
                    <Database className="w-5 h-5 text-dark-500" />
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold">{selectedChart.name}</h3>
                  <p className="text-sm text-dark-400">{selectedChart.repository}</p>
                </div>
              </div>
              <button
                onClick={handleCloseVersionModal}
                className="p-2 hover:bg-dark-700 rounded-lg transition-colors -mr-1"
              >
                <X className="w-5 h-5 text-dark-400" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-dark-400 mb-2">Select Version</label>
                {loadingVersions ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="spinner" />
                  </div>
                ) : (
                  <div className="relative">
                    <select
                      value={selectedVersion}
                      onChange={(e) => setSelectedVersion(e.target.value)}
                      className="input appearance-none pr-10 cursor-pointer text-base sm:text-sm"
                    >
                      {chartVersions.map((v) => (
                        <option key={v.version} value={v.version}>
                          {v.version}
                          {v.appVersion && ` (app: ${v.appVersion})`}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400 pointer-events-none" />
                  </div>
                )}
                {chartVersions.length > 0 && (
                  <p className="text-xs text-dark-500 mt-1">
                    {chartVersions.length} version(s) available
                  </p>
                )}
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-2">
                <button
                  onClick={handleCloseVersionModal}
                  className="btn btn-secondary justify-center"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSession}
                  className="btn btn-primary justify-center"
                  disabled={loadingVersions || !selectedVersion}
                >
                  Open in Editor
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChartCard({ chart, onClick }: { chart: Chart; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className="card hover:border-accent-primary/50 cursor-pointer transition-all duration-200 group active:scale-[0.98]"
    >
      <div className="flex items-start gap-3">
        {chart.icon ? (
          <img
            src={chart.icon}
            alt={chart.name}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-contain bg-dark-700 p-1"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-dark-700 flex items-center justify-center">
            <Database className="w-5 h-5 sm:w-6 sm:h-6 text-dark-500" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-dark-100 truncate group-hover:text-accent-primary transition-colors text-sm sm:text-base">
            {chart.name}
          </h3>
          <p className="text-xs text-dark-500 mt-0.5">
            v{chart.version}
            {chart.appVersion && ` (app: ${chart.appVersion})`}
          </p>
        </div>
        <ExternalLink className="w-4 h-4 text-dark-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      </div>
      {chart.description && (
        <p className="text-xs sm:text-sm text-dark-400 mt-3 line-clamp-2">
          {chart.description}
        </p>
      )}
      {chart.keywords && chart.keywords.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {chart.keywords.slice(0, 3).map((keyword) => (
            <span
              key={keyword}
              className="text-xs bg-dark-700 text-dark-400 px-2 py-0.5 rounded"
            >
              {keyword}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
